import React from "react";
import { Line } from "react-chartjs-2";
import { useTheme } from "@emotion/react";
import styles from "../OrderDetails.module.scss";

import {
  Chart as ChartJS,
  CategoryScale,
  LogarithmicScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Scale,
} from "chart.js";
import {
  dateToWeekDay,
  epochToDateType,
  timeWithLeadingZero,
} from "utils/time";
import {
  addKsuffix,
  digitsToHindi,
  digitsToLatin,
  toMoneyFormat,
} from "@bale/utils";
import { useAppSelector } from "utils/store";
import { AdRecordProps } from "../DetailCard";
import { strings } from "intl/fa";
import { getClickCountMultiplier } from "../../../../../utils/utils";

ChartJS.register(
  CategoryScale,
  LogarithmicScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const LineChart: React.FC<React.PropsWithChildren<AdRecordProps>> = ({ adRecord }) => {
  const selectedAd = useAppSelector((state) => state.ordersList.orderReport);

  const adTime = new Date(selectedAd?.start_time || Date.now());
  const startHour = adTime.getHours();
  const jalaliDateObj = epochToDateType(+adTime);
  const dateInShamsi = `${jalaliDateObj.year}/${jalaliDateObj.month}/${jalaliDateObj.day}`;
  const chartXlabel = digitsToHindi(`${dateToWeekDay(adTime)} ${dateInShamsi}`);

  const theme = useTheme();

  function titletransformer(item: any) {
    const num = item[0].formattedValue.replace("٬", "");
    return `تعداد ${`${
      adRecord.view_count > 0
        ? strings.order_details.views
        : strings.order_details.clicks
    }: `}${
      adRecord.view_count > 0
        ? digitsToHindi(num)
        : digitsToHindi(
            String(
              +digitsToLatin(num) *
                getClickCountMultiplier(adRecord.desired_start_time)
            )
          )
    }`;
  }

  ChartJS.defaults.font.family = "Shabnam";
  const data = {
    labels: [...Array.from(Array(12).keys())].map((item) =>
      timeWithLeadingZero(item + startHour)
    ),
    datasets: [
      {
        data:
          adRecord.view_count > 0
            ? selectedAd?.view_segments
            : selectedAd?.click_segments?.map(
                (click) =>
                  click * getClickCountMultiplier(adRecord.desired_start_time)
              ),
        fill: false,
        borderColor: theme.colors.primary[40], // line color
        tension: 0.3,
      },
    ],
  };
  const options = {
    responsive: true,
    locale: "fa-IR",
    elements: {
      point: {
        pointStyle: "circle",
        backgroundColor: theme.colors.primary[40],
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: chartXlabel,
          padding: 12,
          color: "black",
          font: {
            weight: "bold",
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          callback: function (this: Scale, index: any) {
            return digitsToHindi(String(data.labels[index]));
          },
        },
      },
      y: {
        type: "logarithmic" as const,
        title: {
          display: true,
          text:
            adRecord.view_count > 0
              ? strings.order_details.views
              : strings.order_details.clicks,
          padding: 12,
          color: "black",
          font: {
            weight: "bold",
          },
        },
        grid: {
          borderDashOffset: 10,
          borderDash: [4, 10],
          borderWidth: 0, // to hide axes line
          color: "#B3BAC533", // 33 = .2 opacity
          lineWidth: 2,
        },
        ticks: {
          maxTicksLimit: 6, // default: 11
          autoSkipPadding: 7,
          callback: function (this: Scale, tickValue: any) {
            return toMoneyFormat(String(addKsuffix(tickValue)));
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "nearest" as const,
        rtl: true,
        displayColors: false,
        backgroundColor: theme.colors.dark[400],
        textDirection: "rtl",
        YAlign: "bottom",
        callbacks: {
          title: titletransformer,
          label: () => {
            return "";
          },
        },
      },
    },
  };
  return (
    <div className={styles.Container}>
      <div className={styles.Card}>
        <div className={styles.ChartHeader}>
          {adRecord.view_count > 0
            ? strings.order_details.chart_views_header
            : strings.order_details.chart_clicks_header}
          <strong>
            {digitsToHindi(
              toMoneyFormat(
                String(
                  adRecord.view_count > 0
                    ? selectedAd?.view_count
                    : (selectedAd?.click_count || 1) *
                        getClickCountMultiplier(adRecord.desired_start_time)
                )
              )
            )}
          </strong>
        </div>
        <div className={styles.ViewChart}>
          <Line data={data} options={options}></Line>
        </div>
      </div>
    </div>
  );
};

export default LineChart;
